"use client";

import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Badge } from "@/components/ui/badge";
import { Shuffle } from "lucide-react";

export interface ImageSettings {
  width: number;
  height: number;
  seed: number | null;
  steps: number;
}

interface SettingsPanelProps {
  settings: ImageSettings;
  onChange: (settings: ImageSettings) => void;
  disabled?: boolean;
}

const ASPECT_RATIOS = [
  { label: "1:1", width: 1024, height: 1024 },
  { label: "16:9", width: 1344, height: 768 },
  { label: "9:16", width: 768, height: 1344 },
  { label: "4:3", width: 1152, height: 896 },
  { label: "3:4", width: 896, height: 1152 },
];

export function SettingsPanel({
  settings,
  onChange,
  disabled,
}: SettingsPanelProps) {
  const currentRatio =
    ASPECT_RATIOS.find(
      (r) => r.width === settings.width && r.height === settings.height
    )?.label || "Custom";

  const handleRatioChange = (ratio: (typeof ASPECT_RATIOS)[0]) => {
    onChange({ ...settings, width: ratio.width, height: ratio.height });
  };

  const handleRandomSeed = () => {
    onChange({ ...settings, seed: null });
  };

  return (
    <div className="space-y-6">
      {/* Aspect Ratio */}
      <div className="space-y-3">
        <Label className="text-sm font-medium">Aspect Ratio</Label>
        <div className="flex flex-wrap gap-2">
          {ASPECT_RATIOS.map((ratio) => (
            <Badge
              key={ratio.label}
              variant={currentRatio === ratio.label ? "default" : "secondary"}
              className="cursor-pointer hover:bg-primary/80 transition-colors px-3 py-1"
              onClick={() => !disabled && handleRatioChange(ratio)}
            >
              {ratio.label}
            </Badge>
          ))}
        </div>
        <p className="text-xs text-muted-foreground">
          {settings.width} x {settings.height}
        </p>
      </div>

      {/* Seed */}
      <div className="space-y-3">
        <Label className="text-sm font-medium">Seed</Label>
        <div className="flex gap-2">
          <Input
            type="number"
            value={settings.seed ?? ""}
            onChange={(e) =>
              onChange({
                ...settings,
                seed: e.target.value ? parseInt(e.target.value) : null,
              })
            }
            placeholder="Random"
            className="flex-1 bg-secondary/50"
            disabled={disabled}
          />
          <Button
            variant="outline"
            size="icon"
            onClick={handleRandomSeed}
            disabled={disabled}
            title="Random seed"
          >
            <Shuffle className="h-4 w-4" />
          </Button>
        </div>
        <p className="text-xs text-muted-foreground">
          Use the same seed to reproduce results
        </p>
      </div>

      {/* Inference Steps */}
      <div className="space-y-3">
        <div className="flex justify-between">
          <Label className="text-sm font-medium">Quality (Steps)</Label>
          <span className="text-sm text-muted-foreground">{settings.steps}</span>
        </div>
        <Slider
          value={[settings.steps]}
          onValueChange={([value]) => onChange({ ...settings, steps: value })}
          min={4}
          max={20}
          step={1}
          disabled={disabled}
        />
        <p className="text-xs text-muted-foreground">
          More steps = better quality, slower generation
        </p>
      </div>
    </div>
  );
}
